import React, { useEffect, useRef, useState } from 'react';

/** Width Stripe needs to keep three plans in a horizontal row. */
export const STRIPE_TABLE_WIDTH = 1040;

export type StripePricingTableConfig = {
  pricingTableId: string;
  publishableKey: string;
};

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'stripe-pricing-table': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          'pricing-table-id'?: string;
          'publishable-key'?: string;
          'client-reference-id'?: string;
          'customer-email'?: string;
        },
        HTMLElement
      >;
    }
  }
}

export const ensureStripePricingTableScript = (): void => {
  const scriptSrc = 'https://js.stripe.com/v3/pricing-table.js';
  if (document.querySelector(`script[src="${scriptSrc}"]`)) return;

  const script = document.createElement('script');
  script.src = scriptSrc;
  script.async = true;
  document.body.appendChild(script);
};

/**
 * Responsive Stripe Pricing Table embed.
 * When `interactive` is false, clicks are blocked so the table is view-only.
 */
export const StripePricingTableEmbed = ({
  config,
  clientReferenceId,
  customerEmail,
  interactive = true,
}: {
  config: StripePricingTableConfig;
  clientReferenceId?: string;
  customerEmail?: string;
  interactive?: boolean;
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    ensureStripePricingTableScript();
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    const update = () => {
      const available = viewport.clientWidth;
      setScale(Math.min(1, available / STRIPE_TABLE_WIDTH));
      setContentHeight(content.offsetHeight);
    };

    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(viewport);
    resizeObserver.observe(content);

    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(content, { childList: true, subtree: true });

    const interval = window.setInterval(update, 500);
    const stopInterval = window.setTimeout(() => window.clearInterval(interval), 8_000);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.clearInterval(interval);
      window.clearTimeout(stopInterval);
    };
  }, [config.pricingTableId]);

  return (
    <div className="relative w-full">
      <div
        ref={viewportRef}
        className="w-full overflow-hidden"
        style={{ height: contentHeight ? contentHeight * scale : undefined }}
      >
        <div
          ref={contentRef}
          style={{
            width: STRIPE_TABLE_WIDTH,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <stripe-pricing-table
            pricing-table-id={config.pricingTableId}
            publishable-key={config.publishableKey}
            client-reference-id={clientReferenceId}
            customer-email={customerEmail}
          />
        </div>
      </div>
      {!interactive && (
        <div
          className="absolute inset-0 z-10 cursor-not-allowed"
          aria-hidden
          title="Sign in to choose a plan"
        />
      )}
    </div>
  );
};
